import L from 'leaflet';
import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { geoCodeToAdmCd } from '@/lib/region-code-mapper';

// southkorea/southkorea-maps kostat 2018 (CC BY 4.0) — topo-simple 을 GeoJSON 변환·좌표 4자리 반올림해 자체 호스팅.
// 2012년판은 DMZ 접경 경계가 부정확했다(연천군이 실제보다 24% 크고 파주·철원을 침범).
export const SIDO_URL = '/geo/skorea-provinces-2018.json';
export const SIGUNGU_URL = '/geo/skorea-municipalities-2018.json';

const geoCache: Record<string, GeoJSON.FeatureCollection> = {};

export async function loadGeoJSON(url: string): Promise<GeoJSON.FeatureCollection> {
  if (geoCache[url]) return geoCache[url];
  const res = await fetch(url);
  if (!res.ok) throw new Error(`GeoJSON 로드 실패: ${res.status}`);
  const data = await res.json();
  geoCache[url] = data;
  return data;
}

export function getName(props: Record<string, unknown>): string {
  return String(props.name || props.CTP_KOR_NM || props.SIG_KOR_NM || '');
}

export function getCode(props: Record<string, unknown>): string {
  return String(props.code || props.CTPRVN_CD || props.SIG_CD || '');
}

export function getParentCode(props: Record<string, unknown>): string {
  const code = getCode(props);
  return String(props.province || (code.length >= 2 ? code.substring(0, 2) : ''));
}

export interface RegionInfo {
  code: string;
  name: string;
  level: 'sido' | 'sigungu';
  bounds: L.LatLngBounds;
  geometry?: GeoJSON.Geometry;
}

interface KoreaRegionLayerProps {
  active: boolean;
  selectedSidoCode: string | null;
  onSidoSelect: (code: string, name: string, bounds: L.LatLngBounds) => void;
  onSigunguSelect: (region: RegionInfo) => void;
  /** 통계 단계구분도 색상: SGIS adm_cd → fillColor. 미지정(또는 undefined 반환) 시 기본 스타일. */
  colorByCode?: (admCd: string) => string | undefined;
}

export function KoreaRegionLayer({
  active,
  selectedSidoCode,
  onSidoSelect,
  onSigunguSelect,
  colorByCode,
}: KoreaRegionLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  // 콜백을 ref로 유지 — useEffect 의존성에서 제외해 effect 재실행 방지
  const onSidoSelectRef = useRef(onSidoSelect);
  const onSigunguSelectRef = useRef(onSigunguSelect);
  const colorByCodeRef = useRef(colorByCode);
  onSidoSelectRef.current = onSidoSelect;
  onSigunguSelectRef.current = onSigunguSelect;
  colorByCodeRef.current = colorByCode;

  // 피처별 스타일: 통계 색상이 있으면 채색, 없으면 기본 파란 윤곽
  const styleFn = useCallback((feature?: GeoJSON.Feature): L.PathOptions => {
    const props = (feature?.properties ?? {}) as Record<string, unknown>;
    const fill = colorByCodeRef.current?.(geoCodeToAdmCd(getCode(props)));
    if (fill) {
      return { color: '#6b7280', weight: 1, fillColor: fill, fillOpacity: 0.7 };
    }
    return { color: '#1d4ed8', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.25 };
  }, []);

  useEffect(() => {
    if (!active) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const load = async () => {
      const url = selectedSidoCode ? SIGUNGU_URL : SIDO_URL;
      let data: GeoJSON.FeatureCollection;

      try {
        data = await loadGeoJSON(url);
      } catch (err) {
        console.error('행정구역 GeoJSON 로드 실패:', err);
        return;
      }

      if (cancelled) return;

      // 이전 레이어 제거
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }

      const features = selectedSidoCode
        ? data.features.filter(f => {
            const p = f.properties as Record<string, unknown>;
            return getParentCode(p) === selectedSidoCode;
          })
        : data.features;

      if (features.length === 0) return;

      const geoLayer = L.geoJSON(
        { ...data, features } as GeoJSON.FeatureCollection,
        {
          style: styleFn,
          onEachFeature: (feature, featureLayer) => {
            const props = feature.properties as Record<string, unknown>;
            const name = getName(props);
            const code = getCode(props);

            featureLayer.bindTooltip(name, {
              permanent: false,
              direction: 'center',
              opacity: 0.9,
            });

            featureLayer.on({
              mouseover: (e) => {
                const target = e.target as L.Path;
                target.setStyle({ fillOpacity: 0.45, weight: 2.5, color: '#1d4ed8' });
                target.bringToFront();
              },
              mouseout: (e) => {
                geoLayer.resetStyle(e.target as L.Path);
              },
              click: (e) => {
                const bounds = (e.target as L.Polygon).getBounds();
                if (selectedSidoCode) {
                  onSigunguSelectRef.current({ code, name, level: 'sigungu', bounds, geometry: feature.geometry });
                } else {
                  onSidoSelectRef.current(code, name, bounds);
                  map.fitBounds(bounds, { padding: [30, 30] });
                }
              },
            });
          },
        }
      );

      if (cancelled) return;

      geoLayer.addTo(map);
      layerRef.current = geoLayer;

      if (!selectedSidoCode) {
        map.fitBounds([[33.0, 124.5], [38.9, 131.0]], { padding: [10, 10] });
      }
    };

    load();

    return () => {
      cancelled = true;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [active, selectedSidoCode, map, styleFn]); // 콜백 제외 — ref로 최신값 참조

  // 통계 색상 변경(지표/연도/값) 시 레이어를 다시 그리지 않고 제자리 재채색
  useEffect(() => {
    layerRef.current?.setStyle(styleFn);
  }, [colorByCode, styleFn]);

  return null;
}
